import type { MouseEvent, ReactElement } from 'react';
import type { BoardContents, BoardViewKind } from '@molt/shared';
import { Button } from '@/components/Button';
import type { BoardCardOpenIntent } from '@/lib/board-card-return';
import { cardDisplayTitle } from '@/lib/card-title';
import { useInterfaceLanguage } from '@/lib/interface-language';
import {
  boardViewAccounting,
  boardViewLabel,
  type BoardViewCardSummary,
} from '@/lib/view-state';

export interface BoardViewShellProps {
  view: Exclude<BoardViewKind, 'board'>;
  boardTitle: string;
  contents: BoardContents;
  onOpenCard?: ((intent: BoardCardOpenIntent) => void) | undefined;
  onReturnToBoard: () => void;
}

function openIntent(card: BoardViewCardSummary): BoardCardOpenIntent {
  return {
    cardId: card.id,
    path: card.path,
    stripScrollLeft: 0,
    listScrollTop: 0,
  };
}

export function BoardViewShell({
  view,
  boardTitle,
  contents,
  onOpenCard,
  onReturnToBoard,
}: BoardViewShellProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const label = boardViewLabel(t, view);
  const accounting = boardViewAccounting(view, contents);
  const count = accounting.unrenderableCards.length;
  const hasNotice = accounting.reason !== null && count > 0;

  function handleCardClick(
    event: MouseEvent<HTMLAnchorElement>,
    card: BoardViewCardSummary,
  ) {
    if (onOpenCard === undefined) return;
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    event.preventDefault();
    onOpenCard(openIntent(card));
  }

  return (
    <section
      id="board-view-content"
      aria-label={t('boardFace.viewRegion', { view: label })}
      className="min-h-0 flex-1 overflow-y-auto px-3 pb-3"
    >
      <div className="flex min-h-full flex-col gap-4 rounded-card border border-border-strong bg-panel/90 p-4 text-ink shadow-card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-ink">{label}</h2>
            <p className="mt-1 max-w-2xl text-card text-ink-muted">
              {t('boardView.shell.description', { board: boardTitle, view: label })}
            </p>
          </div>
          <Button type="button" size="sm" variant="secondary" onClick={onReturnToBoard}>
            {t('boardFace.view.board')}
          </Button>
        </div>

        {hasNotice ? (
          <div
            role="status"
            className="rounded-card border border-border bg-surface-raised px-3 py-2 text-card"
          >
            <p className="font-medium text-ink">
              {t('boardView.shell.notShown', { count, view: label }, count)}
            </p>
            <p className="mt-1 text-ink-muted">
              {accounting.reason === 'noDueDate'
                ? t('boardView.shell.reason.noDueDate', { count }, count)
                : t('boardView.shell.reason.noDates', { count }, count)}
            </p>
          </div>
        ) : null}

        {hasNotice ? (
          <ul
            aria-label={t('boardView.shell.hiddenList', { view: label })}
            className="grid gap-2"
          >
            {accounting.unrenderableCards.map((card) => (
              <li
                key={card.id}
                className="rounded-card border border-border bg-surface px-3 py-2"
              >
                <a
                  href={card.path}
                  className="font-medium text-ink underline-offset-2 hover:underline"
                  onClick={(event) => handleCardClick(event, card)}
                >
                  {cardDisplayTitle(t, card.title)}
                </a>
                <p className="mt-1 text-xs text-ink-muted">{card.listTitle}</p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-card text-ink-muted">{t('boardView.shell.allShown')}</p>
        )}
      </div>
    </section>
  );
}
